import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  EMPTY_PREFS, GRID_WORDING, clampWidth, clickSort, prefsKey, readPrefs, toggleSlicer, writePrefs,
  type GridPrefs, type SlicerDef, type SlicerSel, type SortKey, type SortVal,
} from '@lib/grid';

/**
 * Row 356: one table's view state, remembered per operator per table (approval 169). The rules live
 * in `lib/grid.ts`; this hook only holds them and writes them back to the browser.
 */

/** Who is signed in, for the storage key. AppShell provides it; a test or a bare render gets ''. */
export const GridOperatorContext = createContext<string>('');

export interface GridColumn<T> {
  /** Stable key: the remembered layout is keyed on it, so never reuse one for a different column. */
  key: string;
  /** The heading. A string also labels the column in the Columns list. */
  label: ReactNode;
  /** The Columns list's name for a column whose heading is not plain text (a tick box, an icon). */
  menuLabel?: string;
  title?: string;
  render: (row: T, index: number) => ReactNode;
  /** Absent = the heading does not sort. */
  sortValue?: (row: T) => SortVal;
  /** The direction a first click starts in (dates newest first). */
  firstDir?: 'asc' | 'desc';
  /** Never hidden: the column that holds a tick box or the row's own action. */
  locked?: boolean;
  /** An extra the table offers: listed in Columns, shown only once she ticks it. */
  offByDefault?: boolean;
  align?: 'left' | 'right' | 'center';
  thStyle?: CSSProperties;
  tdStyle?: CSSProperties | ((row: T) => CSSProperties | undefined);
  tdClassName?: string | ((row: T) => string | undefined);
  thClassName?: string;
  /** A click inside this cell never reaches the row (a tick box, a button beside a row that opens). */
  stopRowClick?: boolean;
  /** The planned width on a fixed-layout table (`36` px or `'10%'`); absent takes what is left. */
  defaultWidth?: number | string | undefined;
}

export interface GridViewOpts<T> {
  /** The table's own order; [] keeps the order the rows arrive in. */
  defaultSort: SortKey[];
  slicers?: SlicerDef<T>[];
  /** For a paged screen: the sort keys its server accepts (default: every sortable column). */
  serverSortable?: readonly string[] | undefined;
  /**
   * Columns this screen does not use right now (the New tab has no Handled by; only Final Review
   * ticks rows). Left out of the table AND the Columns list, but the operator's layout for them is
   * kept, so a width set on one tab survives a visit to another.
   */
  omit?: readonly string[] | undefined;
}

export interface GridView<T> {
  table: string;
  columns: GridColumn<T>[];
  visible: GridColumn<T>[];
  hidden: ReadonlySet<string>;
  widths: Readonly<Record<string, number>>;
  sort: SortKey[];
  isDefaultSort: boolean;
  slicers: SlicerSel;
  slicerDefs: SlicerDef<T>[];
  expanded: boolean;
  notice: string;
  /** Whether anything differs from the table's own layout (the Reset is offered only then). */
  customized: boolean;
  columnLabel: (c: GridColumn<T>) => string;
  isSortable: (k: string) => boolean;
  clickHeader: (k: string, shift: boolean) => void;
  toggleColumn: (k: string) => void;
  setWidth: (k: string, w: number, save: boolean) => void;
  toggleSlicerValue: (k: string, v: string) => void;
  setSlicerValues: (k: string, vs: string[]) => void;
  clearSlicers: () => void;
  setExpanded: (on: boolean) => void;
  resetColumns: () => void;
  resetAll: () => void;
}

const p0 = (a: string[] | undefined): string[] => a ?? [];

function textOf(n: ReactNode): string {
  return typeof n === 'string' || typeof n === 'number' ? String(n) : '';
}

export function useGridView<T>(table: string, allColumns: GridColumn<T>[], opts: GridViewOpts<T>): GridView<T> {
  const operator = useContext(GridOperatorContext);
  const key = prefsKey(table, operator);
  const slicerDefs = opts.slicers ?? [];
  const omitKey = (opts.omit ?? []).join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => allColumns.filter((c) => !(opts.omit ?? []).includes(c.key)), [allColumns, omitKey]);
  const sortableKeys = useMemo(
    () => opts.serverSortable ?? allColumns.filter((c) => c.sortValue).map((c) => c.key),
    // Column arrays are rebuilt every render; their KEYS are what the layout depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allColumns.map((c) => c.key + (c.sortValue ? '+' : '')).join(','), (opts.serverSortable ?? []).join(',')],
  );
  const shape = useMemo(
    () => ({
      columns: allColumns.map((c) => c.key),
      locked: allColumns.filter((c) => c.locked).map((c) => c.key),
      offByDefault: allColumns.filter((c) => c.offByDefault).map((c) => c.key),
      sortable: sortableKeys,
      slicers: slicerDefs.map((d) => d.key),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allColumns.map((c) => c.key + (c.locked ? '!' : '') + (c.offByDefault ? '?' : '')).join(','), sortableKeys, slicerDefs.map((d) => d.key).join(',')],
  );

  const [prefs, setPrefs] = useState<GridPrefs>(() => readPrefs(key, shape));
  const [expanded, setExpandedState] = useState(false);
  const [notice, setNotice] = useState('');
  const loadedKey = useRef(key);

  // The operator's address can land after the first render (sign-in); read their own layout then.
  useEffect(() => {
    if (loadedKey.current === key) return;
    loadedKey.current = key;
    setPrefs(readPrefs(key, shape));
  }, [key, shape]);

  /** Every change goes through the latest layout (a drag fires many times between renders). Writing
   *  inside the updater is safe: the same layout written twice is the same bytes. */
  const update = useCallback(
    (fn: (p: GridPrefs) => GridPrefs, save = true) => {
      setPrefs((prev) => {
        const next = fn(prev);
        if (save) writePrefs(key, next);
        return next;
      });
    },
    [key],
  );

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(''), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  // Hidden = what she unticked, plus every extra she has not ticked on.
  const hidden = useMemo(() => {
    const on = new Set(prefs.shown ?? []);
    return new Set([...prefs.hidden, ...allColumns.filter((c) => c.offByDefault && !on.has(c.key)).map((c) => c.key)]);
  }, [prefs.hidden, prefs.shown, allColumns]);
  const visible = useMemo(() => columns.filter((c) => !hidden.has(c.key)), [columns, hidden]);
  // A remembered sort on a column this screen leaves out is set aside for now, not forgotten.
  const sort = (prefs.sort ?? opts.defaultSort).filter((s) => columns.some((c) => c.key === s.k) || !allColumns.some((c) => c.key === s.k));
  const firstAsc = (k: string) => columns.find((c) => c.key === k)?.firstDir !== 'desc';

  const view: GridView<T> = {
    table,
    columns,
    visible,
    hidden,
    widths: prefs.widths,
    sort,
    isDefaultSort: prefs.sort === null,
    slicers: prefs.slicers,
    slicerDefs,
    expanded,
    notice,
    customized:
      prefs.hidden.length > 0 || (prefs.shown ?? []).length > 0 || Object.keys(prefs.widths).length > 0 || prefs.sort !== null
      || Object.keys(prefs.slicers).length > 0,
    columnLabel: (c) => c.menuLabel || textOf(c.label) || c.key,
    isSortable: (k) => sortableKeys.includes(k),
    clickHeader: (k, shift) => {
      if (!sortableKeys.includes(k) || !columns.some((c) => c.key === k)) return;
      const r = clickSort(sort, k, shift, firstAsc(k));
      if (r.refused) {
        setNotice(GRID_WORDING.tooManyKeys);
        return;
      }
      update((p) => ({ ...p, sort: r.keys }));
    },
    toggleColumn: (k) => {
      const col = columns.find((c) => c.key === k);
      if (!col || col.locked) return;
      if (col.offByDefault) {
        update((p) => {
          const cur = p0(p.shown);
          const next = cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k];
          const { shown: _drop, ...rest } = p;
          return next.length ? { ...rest, shown: next } : rest;
        });
        return;
      }
      update((p) => ({ ...p, hidden: p.hidden.includes(k) ? p.hidden.filter((x) => x !== k) : [...p.hidden, k] }));
    },
    setWidth: (k, w, save) => update((p) => ({ ...p, widths: { ...p.widths, [k]: clampWidth(w) } }), save),
    toggleSlicerValue: (k, v) => update((p) => ({ ...p, slicers: toggleSlicer(p.slicers, k, v) })),
    setSlicerValues: (k, vs) =>
      update((p) => {
        const s = { ...p.slicers };
        if (vs.length) s[k] = vs;
        else delete s[k];
        return { ...p, slicers: s };
      }),
    clearSlicers: () => update((p) => ({ ...p, slicers: {} })),
    setExpanded: setExpandedState,
    resetColumns: () =>
      update((p) => {
        const { shown: _drop, ...rest } = p;
        return { ...rest, hidden: [], widths: {} };
      }),
    resetAll: () => update(() => ({ ...EMPTY_PREFS })),
  };
  return view;
}
